import {
  PageHeaderWrapper,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { useAppStateContext } from "@src/appV2/App/useAppState";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogErrorEffect } from "@src/appV2/lib/analytics/useLogErrorEffect";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { invalidateShifts } from "../../invalidateShifts";
import { OpenShiftListDataProvider } from "../../Shift/Open/useOpenShiftListDataContext";
import { VirtualShiftListContextProvider } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { PullToRefresh } from "../ListView/PullToRefresh";
import { SHIFT_DISCOVERY_FAVORITE_WORKPLACES_PATH } from "../paths";
import { useShiftModalsDataContext } from "../useShiftModalsDataContext";
import { FavoriteWorkplacesShiftsList } from "./List";
import {
  refetchFavoriteWorkplacesShiftsData,
  useFavoriteWorkplacesShiftsData,
} from "./useFavoriteWorkplacesShiftsData";
import { WorkplacesBottomSheet } from "./WorkplacesBottomSheet";

interface ShiftDiscoveryFavoriteWorkplacesModalProps {
  modalState: UseModalState;
}

export function ShiftDiscoveryFavoriteWorkplacesModal(
  props: ShiftDiscoveryFavoriteWorkplacesModalProps
) {
  const { modalState } = props;
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { setBaseNavigationPathOverride } = useShiftModalsDataContext();
  const pickerModalState = useModalState();
  const {
    state: { isAppActive },
  } = useAppStateContext();

  useScrollToTopOnStatusTap(scrollRef);

  // We need to use useEffect because the open shift hook caches the data
  // So we need to reset cache and refetch to get latest data when app state changed
  useEffect(() => {
    // Refetch favorite workplaces shifts data when worker changes routes or app is resumed
    if (isAppActive) {
      void refetchFavoriteWorkplacesShiftsData(queryClient);
    }
  }, [queryClient, isAppActive]);

  /**
   * When this modal is opened, we want to override the base navigation path
   * so that when shift modals are opened from here, they are rendered on top of this modal
   */
  useEffect(() => {
    // Always set the override when this component is mounted
    // This ensures that even if the component is remounted (e.g., after closing a shift modal),
    // the override is properly restored
    setBaseNavigationPathOverride(SHIFT_DISCOVERY_FAVORITE_WORKPLACES_PATH);

    return () => {
      setBaseNavigationPathOverride(undefined);
    };
  }, [setBaseNavigationPathOverride]);

  const {
    shifts: favoriteWorkplacesShifts,
    isError,
    isLoading,
    isSuccess,
    workplacesMap,
    rateNegotiationsMap,
    shiftAuctionsMap,
    pagination,
  } = useFavoriteWorkplacesShiftsData();

  useLogErrorEffect(
    APP_V2_USER_EVENTS.FAVORITE_WORKPLACES_ERROR,
    {
      error: new Error(
        "Favorite workplaces shifts data failed to load. Check RUM session for debugging."
      ),
    },
    {
      enabled: isError,
    }
  );

  return (
    <FullScreenDialog
      skipTopSafeAreaPadding
      hideBackdrop
      modalState={modalState}
      variant="tertiary"
    >
      <PageHeaderWrapper
        sx={{
          background: (theme) => theme.palette.background.tertiary,
        }}
      >
        <PageHeaderTopRow title="Your favorite workplaces" />
      </PageHeaderWrapper>
      <PullToRefresh
        scrollRef={scrollRef}
        onRefresh={async () => {
          await invalidateShifts(queryClient);
        }}
      />
      <OpenShiftListDataProvider
        openShifts={favoriteWorkplacesShifts}
        workplacesMap={workplacesMap}
        rateNegotiationsMap={rateNegotiationsMap}
        shiftAuctionsMap={shiftAuctionsMap}
        isOpenShiftsLoading={isLoading}
        isOpenShiftsError={isError}
        isOpenShiftsSuccess={isSuccess}
        canLoadMoreShifts={pagination?.hasNextPage ?? false}
        isLoadingMoreShifts={pagination?.isFetchingNextPage ?? false}
        loadMoreShifts={() => {
          void pagination?.fetchNextPage();
        }}
      >
        <VirtualShiftListContextProvider
          shifts={favoriteWorkplacesShifts}
          workplacesMap={workplacesMap}
        >
          <FavoriteWorkplacesShiftsList scrollRef={scrollRef} pickerModalState={pickerModalState} />
        </VirtualShiftListContextProvider>
      </OpenShiftListDataProvider>
      <WorkplacesBottomSheet modalState={pickerModalState} />
    </FullScreenDialog>
  );
}
